import { useAIRecommendation } from '@hakimo-ui/hakimo/data-access';
import { useUser } from '@hakimo-ui/hakimo/util';
import { useAlarmGroupStatic } from '../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { formatEnterpriseAlarmSummary } from '../unified-alarm-group/utils';
import ClickableTimestamp from './ai-recommendation/ClickableTimestamp';
import RecommendationCard from './ai-recommendation/RecommendationCard';
import {
  createTimestampClickHandler,
  getRecommendationStyles,
  useAlarmGroupContext,
} from './ai-recommendation/utils';

interface Props {
  timeZone: string;
}

/**
 * The enterprise alarm's own details, rendered in the card remote guarding
 * gives to the AI recommendation.
 *
 * Enterprise alarms never carry one: the enterprise LLM analyzer writes its
 * classification against the raw alarm, while this card reads the location
 * alarm's AI output, which only the motion analyzer writes. So the card would
 * otherwise sit empty, and the alarm's own type/time/camera take its place.
 *
 * Renders nothing without enterprise details, which is what keeps remote
 * guarding and scan on their real AI recommendation.
 */
export function EnterpriseAlarmCard({ timeZone }: Props) {
  const { alarmGroupId, product, enterpriseAlarmDetails } =
    useAlarmGroupStatic();
  const user = useUser();
  const alarmGroupContext = useAlarmGroupContext();
  const handleTimestampClick = createTimestampClickHandler(
    alarmGroupContext,
    user
  );

  // That enterprise never gets a recommendation is the backend's behaviour,
  // not this code's: if one ever arrives, AIRecommendation renders it and this
  // card would stack a second "Hakimo AI" beside it. Standing down keeps the
  // real recommendation authoritative. Same query key as AIRecommendation's,
  // so this shares its cache rather than issuing a second request.
  const { data: aiData } = useAIRecommendation(alarmGroupId, product);
  const hasRecommendation = Boolean(aiData?.[0]?.recommendations);

  if (!enterpriseAlarmDetails || hasRecommendation) {
    return null;
  }

  // The zone is named in the card footer and read back by the click handler,
  // so it has to be the one the summary was formatted in.
  const zone = timeZone || 'UTC';
  const summary = formatEnterpriseAlarmSummary(enterpriseAlarmDetails, zone);

  if (!summary) {
    return null;
  }

  const { alarmType } = enterpriseAlarmDetails;

  // RG's badge is always the neutral `info` variant; only the headline takes
  // the verdict colour, and enterprise is always an escalate.
  const styles = getRecommendationStyles();
  const headlineStyles = getRecommendationStyles('deny');

  return (
    <RecommendationCard
      // Enterprise alarms carry no recommendation to vary this on; they always
      // want an operator, so the label is fixed.
      badge={{ label: 'ESCALATE', className: styles.badgeClasses }}
      headline={
        alarmType
          ? {
              icon: headlineStyles.icon,
              text: alarmType,
              className: 'text-alert-600',
            }
          : undefined
      }
      body={
        <div className="pr-2">
          <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">
            <ClickableTimestamp
              text={summary}
              onTimestampClick={handleTimestampClick}
            />
          </p>
        </div>
      }
      timeZone={zone}
    />
  );
}

export default EnterpriseAlarmCard;
